import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

export interface BackingMixingCycleData {
  cycleNum: string;
  product: string;
  executiveName: string;
  lineNo: string;
  startTime: string;
  standardMixingTime: string;
  mixingTime: string;
  mixingOutTime: string;
  waterTemp: string;
  yeastSlurryTemp: string;
  oilTemp: string;
  iceKg: string;
  doughTemp: string;
  doughPh: string;
  dividingWeight10: string;
  seedVisibility: string;
}

interface OfflineSavedData {
  cycleNo: number;
  records: BackingMixingCycleData[];
  timestamp: number;
}

interface BackingMixingState {
  fetchedCycles: BackingMixingCycleData[];
  offlineSavedData: OfflineSavedData[];
  isLoading: boolean;
  isSyncing: boolean;
  error: string | null;
  lastFetchTimestamp: number | null;
}

const initialState: BackingMixingState = {
  fetchedCycles: [],
  offlineSavedData: [],
  isLoading: false,
  isSyncing: false,
  error: null,
  lastFetchTimestamp: null,
};

const backingMixingSlice = createSlice({
  name: 'backingMixing',
  initialState,
  reducers: {
    addOfflineData: (state, action: PayloadAction<OfflineSavedData>) => {
      state.offlineSavedData.push(action.payload);
    },
    clearOfflineData: (state) => {
      state.offlineSavedData = [];
    },
    removeOfflineDataItem: (state, action: PayloadAction<number>) => {
      state.offlineSavedData = state.offlineSavedData.filter(
        item => item.cycleNo !== action.payload
      );
    },
    setFetchedCycles: (state, action: PayloadAction<BackingMixingCycleData[]>) => {
      state.fetchedCycles = action.payload;
      state.lastFetchTimestamp = Date.now();
    },
    clearError: (state) => {
      state.error = null;
    },
    resetState: (state) => {
      state.fetchedCycles = [];
      state.offlineSavedData = [];
      state.isLoading = false;
      state.isSyncing = false;
      state.error = null;
      state.lastFetchTimestamp = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSyncing: (state, action: PayloadAction<boolean>) => {
      state.isSyncing = action.payload;
    },
  },
});

export const {
  addOfflineData,
  clearOfflineData,
  removeOfflineDataItem,
  setFetchedCycles,
  clearError,
  resetState,
  setLoading,
  setSyncing,
} = backingMixingSlice.actions;

export const selectBackingMixingState = (state: { backingMixing: BackingMixingState }) => state.backingMixing;
export const selectOfflineDataCount = (state: { backingMixing: BackingMixingState }) => state.backingMixing.offlineSavedData.length;
export const selectFetchedCycles = (state: { backingMixing: BackingMixingState }) => state.backingMixing.fetchedCycles;

export default backingMixingSlice.reducer;
